import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Skeleton,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LanguageIcon from '@mui/icons-material/Language';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { colors, typography } from '../../styles/theme';
import { authInputAutofillSx } from '../../Access/styles/authPanel.styles';
import { toast } from '../../Components/Toast';
import {
  getShareDialogFooterScrollHintSx,
  useShareDialogScrollHint,
} from '../../Components/shareDialogScrollHint';
import {
  getShareDangerButtonSx,
  getSharePrimaryButtonSx,
} from '../../Components/shareDialogButtonSx';
import { FORM_SWITCH_SX } from '../../Map/components/DockPanel/Tabs/formBlockUi';
import { useShareDialogBrandsOverlap } from '../../Components/BrandsModal/BrandsModalContext';
import ShareTemplateSection, {
  normalizeShareBrandId,
  normalizeShareTemplate,
  readShareApiError,
  resolveShareAppearance,
  SHARE_TEMPLATE_DEFAULT,
} from '../../Components/ShareTemplateSection';
import { getMyBrands } from '../../services/brandsService';
import { getCadProject, updateCadProject } from '../../services/cadService';
import { useMetaShareCutGuard } from '../../MetaAds/components/MetaShareCutDialog';
import { forgetMetaAutoShare } from '../../MetaAds/metaCreateShare';
import { isCadMapDisplay } from '../utils/cadGeometry';
import { buildShowroomPath } from '../utils/cadRegistros';
import {
  cadAllowlistFromEmails,
  emailsFromCadAllowlist,
  getCadProjectId,
  isCadForbidden,
  isCadProjectShared,
  isCadUnauthorized,
  mergeCadProjectSummary,
  readCadInvite,
} from '../utils/cadAccess';
import {
  SHARE_PERSON_ROLE_EDITOR,
  SHARE_PERSON_ROLE_VIEWER,
  loadShareEditorEmails,
  mergeSharePeopleEmails,
  syncShareEditors,
} from '../../Map/utils/sharePeopleRoles';
import SharePersonRoleSelect from '../../Components/SharePersonRoleSelect';
import { resolveSpaceGuestWriteError } from '../../Map/utils/spaceMirrorLots';

const BG_PAPER = colors.backgroundSurface;
const BG_ELEVATED = colors.backgroundDark;
const BORDER = colors.border;
const TEXT_PRIMARY = '#F9FAFB';
const TEXT_SECONDARY = '#ABABAB';
const TEXT_MUTED = 'rgba(255,255,255,0.45)';
const DANGER = '#EE5757';

const SHARE_SECTION_SHELL_SX = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  border: `1px solid ${BORDER}`,
  borderRadius: '8px',
  p: 1.5,
};

const SHARE_EMAIL_INPUT_SX = {
  flex: 1,
  minWidth: 140,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  color: TEXT_PRIMARY,
  fontFamily: typography.fontFamily.primary,
  fontSize: '13px',
  padding: '2px 0',
  ...authInputAutofillSx,
};

const SHARE_DIALOG_SCROLLBAR_SX = {
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(255,255,255,0.10) transparent',
  '&::-webkit-scrollbar': { width: 6 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(255,255,255,0.10)',
    borderRadius: 8,
  },
};

const SHARE_DIALOG_PAPER_SX = {
  bgcolor: BG_PAPER,
  borderRadius: '12px',
  overflow: 'hidden',
  maxHeight: '80vh',
  display: 'flex',
  flexDirection: 'column',
};

const SHARE_DIALOG_CONTENT_SX = {
  px: 2.5,
  pt: 2,
  pb: 1,
  flex: '1 1 auto',
  minHeight: 0,
  overflowY: 'auto',
  ...SHARE_DIALOG_SCROLLBAR_SX,
};

const SHARE_DIALOG_LIST_META_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  width: '100%',
  flexShrink: 0,
  px: 2.5,
  pt: 2,
  pb: 0.5,
  bgcolor: BG_PAPER,
};

const INVITE_LIST_SX = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
  maxHeight: 160,
  overflowY: 'auto',
  pr: 0.5,
  ...SHARE_DIALOG_SCROLLBAR_SX,
  '&::-webkit-scrollbar': { width: 5 },
};

const skTextSx = { bgcolor: 'rgba(255,255,255,0.10)', borderRadius: 1 };
const skFooterButtonSx = {
  bgcolor: 'rgba(255,255,255,0.10)',
  borderRadius: '8px',
  flexShrink: 0,
};

function buildShowroomUrl({ id } = {}) {
  const path = buildShowroomPath({ id });
  return path === '/lista' ? '' : `${window.location.origin}/#${path}`;
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

function normalizeEmail(raw) {
  return String(raw || '').trim().toLowerCase();
}

function getEmailInitials(email) {
  const local = String(email || '').split('@')[0] || '';
  const parts = local.split(/[._\-+]+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  }
  const clean = local.replace(/[^a-zA-Z0-9]/g, '');
  if (clean.length >= 2) return clean.slice(0, 2).toUpperCase();
  if (clean.length === 1) return clean.toUpperCase();
  return '?';
}

function sameEmailSet(a = [], b = []) {
  if (a.length !== b.length) return false;
  const left = [...a].map(String).sort();
  const right = [...b].map(String).sort();
  return left.every((value, idx) => value === right[idx]);
}

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      return true;
    } catch {
      return false;
    }
  }
}

function AccessModeCard({ selected, icon, title, description, onClick, disabled }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 0.75,
        p: 1.5,
        m: 0,
        textAlign: 'left',
        cursor: disabled ? 'default' : 'pointer',
        borderRadius: '8px',
        border: selected ? `1.5px solid ${colors.primary}` : `1px solid ${BORDER}`,
        bgcolor: selected ? 'rgba(72,146,145,0.12)' : BG_ELEVATED,
        color: 'inherit',
        font: 'inherit',
        transition: 'border-color 0.15s, background-color 0.15s',
        opacity: disabled ? 0.6 : 1,
        '&:hover': disabled ? undefined : {
          borderColor: selected ? colors.primary : 'rgba(255,255,255,0.28)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ color: selected ? colors.primaryLight : TEXT_SECONDARY, display: 'flex' }}>
          {icon}
        </Box>
        <Typography sx={{ color: TEXT_PRIMARY, fontSize: '13px', fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      <Typography sx={{ color: TEXT_MUTED, fontSize: '11px', lineHeight: 1.35 }}>
        {description}
      </Typography>
    </Box>
  );
}

function ShareDialogContentSkeleton() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box>
        <Skeleton variant="text" width="40%" height={16} sx={skTextSx} />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
          <Skeleton variant="rounded" height={72} sx={skTextSx} />
          <Skeleton variant="rounded" height={72} sx={skTextSx} />
        </Box>
      </Box>
      <Skeleton variant="rounded" width="100%" height={44} sx={skTextSx} />
    </Box>
  );
}

/** Toast de invite SES: OK si sent, warning si skipped / failed. */
function toastCadInviteOutcome(invite, { updatingPrivateAccess, shouldNotify, isPublic = false }) {
  const failed = Array.isArray(invite?.failed) ? invite.failed.filter(Boolean) : [];
  const skipped = invite?.skipped === true;
  const sent = !skipped && (invite?.sent === true || (shouldNotify && !invite));
  const linkLabel = isPublic ? 'enlace' : 'enlace privado';

  if (updatingPrivateAccess && shouldNotify && sent) {
    toast.success(
      isPublic
        ? 'Se notificó a los nuevos editores.'
        : 'Se notificó a las nuevas personas con acceso.',
      { title: isPublic ? 'Editores actualizados.' : 'Accesos actualizados.' },
    );
  } else if (updatingPrivateAccess) {
    toast.success(
      isPublic
        ? 'Ya podés compartir el enlace con los editores.'
        : 'Ya podés compartir el enlace con las nuevas personas.',
      { title: isPublic ? 'Editores actualizados.' : 'Accesos actualizados.' },
    );
  } else if (shouldNotify && sent) {
    toast.success(`Se notificó a las personas y el ${linkLabel} quedó activo.`, {
      title: 'Accesos asignados.',
    });
  } else {
    toast.success(
      isPublic
        ? 'Copiá el enlace y compartilo con quien quieras.'
        : 'Se generó un enlace privado, y está disponible para compartir.',
      { title: isPublic ? 'Enlace generado.' : 'Accesos asignados.' },
    );
  }

  if (failed.length > 0) {
    toast.warning(`No se pudo notificar a: ${failed.join(', ')}`);
  } else if (skipped) {
    toast.warning(invite?.reason || 'No se envió la invitación por email.');
  }
}

/**
 * Compartir lista CAD. Misma UI que listas catastrales; lookup por `id` (no
 * `list_id`), sin `scope`. Marca + invite SES. Personas: restricted = ver/editor;
 * público = solo editor (+ notify con `as_editor`). Sacar editor =
 * `DELETE /deletecadprojectmember`.
 *
 * `ownerUserId` (espacio ajeno, F3-CAD): el invitado comparte el showroom del
 * owner. Lee con `?owner_user_id=` (exige `can_share`), manda solo claves de
 * share con `owner_user_id` en el body (PATCH parcial: la marca, el catastro de
 * fondo y la consulta quedan como las dejó el owner), apariencia fija
 * (`ShareTemplateSection` `locked`) y sin invitar editores (`as_editor` → 403).
 * Sin `ownerUserId` → igual que siempre. Ver docs/maps/espacios-invite.md.
 */
export default function ShareCadProjectDialog({
  open,
  slug,
  projectId = '',
  projectName = '',
  ownerUserId = null,
  onClose,
  onSaved,
}) {
  const spaceOwner = ownerUserId ? String(ownerUserId).trim() : '';
  const isSpaceGuest = Boolean(spaceOwner);
  const brandsOverlap = useShareDialogBrandsOverlap();
  const [accessMode, setAccessMode] = useState('public');
  const [emailInput, setEmailInput] = useState('');
  const [emailInputError, setEmailInputError] = useState('');
  const [emails, setEmails] = useState([]);
  const [originalEmails, setOriginalEmails] = useState([]);
  const [editorEmails, setEditorEmails] = useState([]);
  const [originalEditorEmails, setOriginalEditorEmails] = useState([]);
  const [notifyUsers, setNotifyUsers] = useState(false);
  const [notifyMessage, setNotifyMessage] = useState('');
  const [notifyMessageError, setNotifyMessageError] = useState('');
  const [shareTemplate, setShareTemplate] = useState(SHARE_TEMPLATE_DEFAULT);
  const [savedShareTemplate, setSavedShareTemplate] = useState(SHARE_TEMPLATE_DEFAULT);
  /** `null` = seguí la marca principal. */
  const [brandId, setBrandId] = useState(null);
  const [savedBrandId, setSavedBrandId] = useState(null);
  const [showCadastralBase, setShowCadastralBase] = useState(false);
  const [savedShowCadastralBase, setSavedShowCadastralBase] = useState(false);
  const [cadastralAvailable, setCadastralAvailable] = useState(false);
  const [resolvedProjectId, setResolvedProjectId] = useState(() => String(projectId || '').trim());
  const [loading, setLoading] = useState(false);
  const [shareConfigLoaded, setShareConfigLoaded] = useState(false);
  const [linkStatus, setLinkStatus] = useState('none');
  const [savedAccessMode, setSavedAccessMode] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  /** Espacio ajeno: sin la config del owner (o sin `can_share`) no se guarda. */
  const [guestConfigMissing, setGuestConfigMissing] = useState(false);
  const inputRef = useRef(null);
  // Meta (t1099u): antes de cortar el link público con campañas de Terrio de sus parcelas, confirmación
  // con «Pausar y seguir». Solo el dueño en su espacio.
  const { confirmCut, guardDialog } = useMetaShareCutGuard({ enabled: !isSpaceGuest });

  const effectiveProjectId = resolvedProjectId || String(projectId || '').trim();
  const projectLookup = useMemo(
    () => (effectiveProjectId ? { id: effectiveProjectId } : { id: '' }),
    [effectiveProjectId],
  );
  const shareUrl = useMemo(
    () => (effectiveProjectId ? buildShowroomUrl({ id: effectiveProjectId }) : ''),
    [effectiveProjectId],
  );
  const isInitializing = loading || (open && !shareConfigLoaded);
  const isPrivate = accessMode === 'restricted';
  const linkMatchesTab = savedAccessMode === accessMode;
  const effectiveLinkStatus = linkMatchesTab ? linkStatus : 'none';
  const hasActiveLink = effectiveLinkStatus === 'active';
  const allowlistDirty = !sameEmailSet(emails, originalEmails);
  const editorsDirty = !sameEmailSet(editorEmails, originalEditorEmails);
  const hasNewInvitees = emails.some((email) => !originalEmails.includes(email));
  const templateDirty = shareTemplate !== savedShareTemplate;
  const brandDirty = brandId !== savedBrandId;
  const cadastralDirty = cadastralAvailable && showCadastralBase !== savedShowCadastralBase;
  const privatePrimaryLabel = (hasActiveLink || (linkMatchesTab && originalEmails.length > 0))
    ? 'Actualizar accesos'
    : 'Dar acceso';
  const privatePrimaryDisabled = privatePrimaryLabel === 'Dar acceso'
    ? emails.length === 0
    : !allowlistDirty && !editorsDirty && !cadastralDirty && !templateDirty && !brandDirty;
  /** Público: Personas = solo editores. */
  const publicDirty = editorsDirty || cadastralDirty || templateDirty || brandDirty;
  const scrollHintKey = [open, isPrivate, isInitializing, emails.length, shareTemplate, effectiveLinkStatus].join('|');
  const { contentRef, canScrollMore } = useShareDialogScrollHint(scrollHintKey);
  const guestBlocked = isSpaceGuest && guestConfigMissing;

  useEffect(() => {
    if (!open) return;
    setAccessMode('public');
    setEmailInput('');
    setEmailInputError('');
    setEmails([]);
    setOriginalEmails([]);
    setEditorEmails([]);
    setOriginalEditorEmails([]);
    setNotifyUsers(false);
    setNotifyMessage('');
    setNotifyMessageError('');
    setShareTemplate(SHARE_TEMPLATE_DEFAULT);
    setSavedShareTemplate(SHARE_TEMPLATE_DEFAULT);
    setBrandId(null);
    setSavedBrandId(null);
    setShowCadastralBase(false);
    setSavedShowCadastralBase(false);
    setCadastralAvailable(false);
    setLoading(true);
    setShareConfigLoaded(false);
    setLinkStatus('none');
    setSavedAccessMode(null);
    setSubmitting(false);
    setError('');
    setGuestConfigMissing(false);
  }, [open]);

  useEffect(() => {
    if (hasNewInvitees) return;
    setNotifyUsers(false);
    setNotifyMessage('');
    setNotifyMessageError('');
  }, [hasNewInvitees]);

  useEffect(() => {
    let cancelled = false;
    if (!open || !projectId) return undefined;

    setLoading(true);
    setError('');
    setResolvedProjectId(String(projectId || '').trim());

    (async () => {
      try {
        const result = isSpaceGuest
          ? await getCadProject({ id: projectId }, { ownerUserId: spaceOwner })
          : await getCadProject({ id: projectId });
        if (cancelled) return;
        if (isCadUnauthorized(result._httpStatus)) {
          setError('Iniciá sesión para compartir esta lista.');
          if (isSpaceGuest) setGuestConfigMissing(true);
          setShareConfigLoaded(true);
          setLoading(false);
          return;
        }
        if (!result.ok || !result.project) {
          setError(
            (isSpaceGuest && resolveSpaceGuestWriteError(result._httpStatus, spaceOwner))
            || result.message
            || 'No pudimos cargar la configuración. Intentalo de nuevo.',
          );
          if (isSpaceGuest) setGuestConfigMissing(true);
          setShareConfigLoaded(true);
          setLoading(false);
          return;
        }

        const project = result.project;
        // Espacio ajeno: sin `can_share` el GET no es el del diálogo (no trae
        // la allowlist) y guardar pisaría la config del owner.
        if (isSpaceGuest && project?.can_share !== true) {
          setError(resolveSpaceGuestWriteError(403, spaceOwner));
          setGuestConfigMissing(true);
          setShareConfigLoaded(true);
          setLoading(false);
          return;
        }
        const nextId = getCadProjectId(project);
        if (nextId) setResolvedProjectId(nextId);
        // Espacio ajeno: los editores por showroom son del dueño
        // (getcadprojectmembers es solo owner), así que no se piden.
        const memberEmails = nextId && !isSpaceGuest
          ? await loadShareEditorEmails('cad', nextId)
          : [];
        if (cancelled) return;
        const shared = isCadProjectShared(project);
        const mode = project?.access_mode === 'restricted' ? 'restricted' : 'public';
        setAccessMode(shared ? mode : 'public');
        setSavedAccessMode(shared ? mode : null);
        setLinkStatus(shared ? 'active' : 'none');
        const nextTemplate = normalizeShareTemplate(project?.share_template);
        setShareTemplate(nextTemplate);
        setSavedShareTemplate(nextTemplate);
        const nextBrandId = normalizeShareBrandId(project?.brand_id);
        setBrandId(nextBrandId);
        setSavedBrandId(nextBrandId);
        const cadastralOnMap = isCadMapDisplay(project);
        setCadastralAvailable(cadastralOnMap);
        const nextCadastral = cadastralOnMap && project?.show_cadastral_base === true;
        setShowCadastralBase(nextCadastral);
        setSavedShowCadastralBase(nextCadastral);
        if (shared && mode === 'restricted') {
          const nextEmails = mergeSharePeopleEmails(
            emailsFromCadAllowlist(project.allowlist),
            memberEmails,
          );
          setEmails(nextEmails);
          setOriginalEmails(nextEmails);
        } else {
          // Público (o no shared): Personas lista solo editores.
          setEmails([...memberEmails]);
          setOriginalEmails([...memberEmails]);
        }
        setEditorEmails([...memberEmails]);
        setOriginalEditorEmails([...memberEmails]);
        setShareConfigLoaded(true);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error('CAD share: no se pudo cargar el proyecto', err);
        setError('No pudimos cargar la configuración. Intentalo de nuevo.');
        if (isSpaceGuest) setGuestConfigMissing(true);
        setShareConfigLoaded(true);
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, projectId, slug, spaceOwner, isSpaceGuest]);

  const tryCommitEmail = () => {
    const value = normalizeEmail(emailInput);
    if (!value) return;
    if (!isValidEmail(value)) {
      setEmailInputError('El email no tiene un formato válido.');
      return;
    }
    if (emails.includes(value)) {
      setEmailInputError('Ese email ya fue agregado.');
      return;
    }
    setEmails((prev) => [...prev, value]);
    if (accessMode === 'public') {
      setEditorEmails((prev) => (prev.includes(value) ? prev : [...prev, value]));
    }
    setEmailInput('');
    setEmailInputError('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleEmailKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      tryCommitEmail();
    }
    if (event.key === 'Backspace' && !emailInput && emails.length > 0) {
      const removed = emails[emails.length - 1];
      setEmails((prev) => prev.slice(0, -1));
      setEditorEmails((prev) => prev.filter((item) => item !== removed));
      setEmailInputError('');
    }
  };

  const handleEmailBlur = () => {
    if (emailInput.trim()) tryCommitEmail();
  };

  const handleEmailChange = (event) => {
    const raw = event.target.value;
    if (raw.includes(',') || raw.includes(';')) {
      const parts = raw.split(/[,;]+/).map(normalizeEmail).filter(Boolean);
      const valid = [];
      const invalid = [];
      parts.forEach((part) => {
        if (!isValidEmail(part)) { invalid.push(part); return; }
        if (!emails.includes(part)) valid.push(part);
      });
      if (valid.length) {
        setEmails((prev) => [...prev, ...valid]);
        if (accessMode === 'public') {
          setEditorEmails((prev) => {
            const next = [...prev];
            valid.forEach((email) => {
              if (!next.includes(email)) next.push(email);
            });
            return next;
          });
        }
      }
      if (invalid.length) {
        setEmailInput(invalid.join(', '));
        setEmailInputError(`Algunos emails no son válidos: ${invalid.join(', ')}`);
      } else {
        setEmailInput('');
        setEmailInputError('');
        setTimeout(() => inputRef.current?.focus(), 0);
      }
      return;
    }
    setEmailInput(raw);
    if (emailInputError) setEmailInputError('');
  };

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const applySaved = (project, mode, nextEmails, nextEditors) => {
    onSaved?.(project);
    setSavedAccessMode(mode);
    setLinkStatus('active');
    setSavedShareTemplate(normalizeShareTemplate(project?.share_template || shareTemplate));
    setShareTemplate(normalizeShareTemplate(project?.share_template || shareTemplate));
    // Con `default` el backend limpia la marca: el estado local se alinea con
    // lo que quedó guardado en vez de conservar la última elección.
    const nextBrandId = Object.prototype.hasOwnProperty.call(project || {}, 'brand_id')
      ? normalizeShareBrandId(project.brand_id)
      : brandId;
    setSavedBrandId(nextBrandId);
    setBrandId(nextBrandId);
    const savedCadastral = project?.show_cadastral_base;
    const nextCadastral = typeof savedCadastral === 'boolean' ? savedCadastral : showCadastralBase;
    setShowCadastralBase(nextCadastral);
    setSavedShowCadastralBase(nextCadastral);
    setOriginalEmails([...nextEmails]);
    setEmails([...nextEmails]);
    if (Array.isArray(nextEditors)) {
      setEditorEmails([...nextEditors]);
    }
  };

  const handleSubmit = async () => {
    if (!slug && !effectiveProjectId) return;
    if (guestBlocked) return;
    if (accessMode === 'restricted' && privatePrimaryDisabled && !notifyUsers) return;
    if (accessMode === 'public' && hasActiveLink && !publicDirty && !notifyUsers) return;

    if (emailInput.trim()) {
      const value = normalizeEmail(emailInput);
      if (!isValidEmail(value)) {
        setEmailInputError('El email no tiene un formato válido.');
        return;
      }
      if (!emails.includes(value)) {
        setEmails((prev) => [...prev, value]);
        if (accessMode === 'public') {
          setEditorEmails((prev) => (prev.includes(value) ? prev : [...prev, value]));
        }
      }
      setEmailInput('');
      setEmailInputError('');
    }

    const finalEmails = emailInput.trim()
      ? [...emails, normalizeEmail(emailInput)].filter((value, idx, all) => all.indexOf(value) === idx)
      : emails;
    const updatingPrivateAccess = accessMode === 'restricted'
      && (hasActiveLink || originalEmails.length > 0);

    if (accessMode === 'restricted' && finalEmails.length === 0 && !updatingPrivateAccess) {
      setError('Agregá al menos un email para poder continuar.');
      return;
    }

    const originalSet = new Set(originalEmails.map((email) => normalizeEmail(email)));
    const newInviteEmails = finalEmails.filter((email) => !originalSet.has(normalizeEmail(email)));
    // Espacio ajeno: invitar editores es del dueño (`as_editor` → 403).
    const nextEditors = isSpaceGuest
      ? []
      : (accessMode === 'public'
        ? finalEmails
        : editorEmails.filter((email) => finalEmails.includes(email)));
    const inviteMessage = notifyMessage.trim().slice(0, 2000);
    const wantsNotify = notifyUsers && newInviteEmails.length > 0;
    if (notifyUsers && !inviteMessage) {
      setNotifyMessageError('Escribí un mensaje para notificar a los usuarios.');
      return;
    }
    if (notifyUsers && newInviteEmails.length === 0) {
      setNotifyMessageError('Solo se notifica a personas nuevas. Agregá al menos un email nuevo.');
      return;
    }
    // Espacio ajeno + público: el invite público es solo para editores (del dueño).
    const shouldNotify = wantsNotify && !(isSpaceGuest && accessMode === 'public');

    let allowlist;
    if (accessMode === 'restricted') {
      allowlist = cadAllowlistFromEmails(finalEmails, { asEditorEmails: nextEditors });
    } else if (shouldNotify) {
      allowlist = cadAllowlistFromEmails(newInviteEmails, { asEditorEmails: newInviteEmails });
    }

    setError('');
    setSubmitting(true);
    try {
      // Pasa de público a privado: los anuncios de sus parcelas pedirían iniciar sesión.
      if (accessMode === 'restricted' && linkStatus === 'active' && savedAccessMode === 'public') {
        const goOn = await confirmCut({ projectId: effectiveProjectId }, 'restrict');
        if (!goOn) return;
      }
      let appearanceBody = {};
      if (!isSpaceGuest) {
        const brandsRes = await getMyBrands();
        const appearance = resolveShareAppearance(
          shareTemplate,
          brandId,
          brandsRes.ok ? brandsRes.brands : [],
        );
        // share_template siempre: si borró las identidades no reenviamos personalized.
        appearanceBody = {
          share_template: appearance.shareTemplate,
          brand_id: appearance.shareTemplate === SHARE_TEMPLATE_DEFAULT
            ? null
            : appearance.brandId,
          ...(cadastralAvailable ? { show_cadastral_base: showCadastralBase } : {}),
        };
      }
      // CAD: body con id (no list_id) y sin scope. Resto igual que listas.
      // Espacio ajeno: solo claves de share + owner_user_id. Marca, catastro de
      // fondo y consulta no se mandan (PATCH parcial → quedan las del owner;
      // show_cadastral_base del invitado → 403).
      const result = await updateCadProject({
        ...projectLookup,
        is_shared: true,
        access_mode: accessMode,
        ...appearanceBody,
        ...(isSpaceGuest ? { ownerUserId: spaceOwner } : {}),
        ...(allowlist ? { allowlist } : {}),
        ...(shouldNotify
          ? {
            send_invite: true,
            invite_message: inviteMessage,
            invite_emails: newInviteEmails,
          }
          : {}),
      });
      const guestError = isSpaceGuest && !result.ok
        ? resolveSpaceGuestWriteError(result._httpStatus, spaceOwner)
        : null;
      if (guestError) {
        setError(guestError);
        setSubmitting(false);
        return;
      }
      if (isCadForbidden(result._httpStatus)) {
        setError(result.message || 'No podés compartir esta lista.');
        setSubmitting(false);
        return;
      }
      if (!result.ok) {
        setError(readShareApiError(
          result,
          'No se pudo guardar la configuración. Intentalo de nuevo.',
        ));
        setSubmitting(false);
        return;
      }

      const project = mergeCadProjectSummary({ ...projectLookup, slug }, result.project);
      const savedId = getCadProjectId(project);
      if (savedId) setResolvedProjectId(savedId);
      // El dueño guardó el share a mano: ya no es «solo para el anuncio» (Meta, paso 3).
      if (!isSpaceGuest) forgetMetaAutoShare({ scope: 'project', id: savedId || effectiveProjectId });
      applySaved(project, accessMode, finalEmails, nextEditors);

      // Sacar editor = DELETE /deletecadprojectmember (no unshare).
      // Espacio ajeno: los editores son del dueño, no se tocan.
      const editorId = savedId || effectiveProjectId;
      const synced = isSpaceGuest
        ? { ok: true }
        : await syncShareEditors(
          'cad',
          editorId,
          nextEditors,
          originalEditorEmails,
        );
      setOriginalEditorEmails(synced.ok ? nextEditors : originalEditorEmails);
      if (!synced.ok) {
        toast.warning(
          accessMode === 'restricted'
            ? 'Se guardó quién puede ver, pero no se pudieron actualizar todos los editores.'
            : 'Se guardó el enlace, pero no se pudieron actualizar todos los editores.',
        );
      }
      void copyToClipboard(shareUrl);

      if (accessMode === 'restricted') {
        toastCadInviteOutcome(readCadInvite(result), {
          updatingPrivateAccess,
          shouldNotify,
        });
      } else if (shouldNotify) {
        toastCadInviteOutcome(readCadInvite(result), {
          updatingPrivateAccess: hasActiveLink,
          shouldNotify: true,
          isPublic: true,
        });
      } else {
        toast.success(
          editorsDirty && hasActiveLink
            ? 'Ya podés compartir el enlace con los editores.'
            : 'Copiá el enlace y compartilo con quien quieras.',
          {
            title: hasActiveLink
              ? (editorsDirty ? 'Editores actualizados.' : 'Enlace actualizado.')
              : 'Enlace generado.',
          },
        );
      }
      if (shouldNotify) {
        setNotifyUsers(false);
        setNotifyMessage('');
        setNotifyMessageError('');
      }
    } catch (err) {
      console.error('CAD share: fallo al guardar', err);
      setError('No se pudo guardar la configuración. Intentalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async () => {
    if (!slug && !effectiveProjectId) return;
    if (guestBlocked) return;
    setSubmitting(true);
    setError('');
    try {
      if (linkStatus === 'active' && savedAccessMode === 'public') {
        const goOn = await confirmCut({ projectId: effectiveProjectId }, 'unshare');
        if (!goOn) return;
      }
      // Espacio ajeno: mismo apagado (`is_shared: false`) con owner_user_id.
      const result = await updateCadProject({
        ...projectLookup,
        is_shared: false,
        ...(isSpaceGuest ? { ownerUserId: spaceOwner } : {}),
      });
      if (!result.ok) {
        setError(
          (isSpaceGuest && resolveSpaceGuestWriteError(result._httpStatus, spaceOwner))
          || result.message
          || 'No se pudo desactivar el enlace. Intentalo de nuevo.',
        );
        setSubmitting(false);
        return;
      }
      onSaved?.(mergeCadProjectSummary({ slug }, result.project));
      if (!isSpaceGuest) forgetMetaAutoShare({ scope: 'project', id: effectiveProjectId });
      setLinkStatus('none');
      setSavedAccessMode(null);
      setOriginalEmails([]);
      setEmails(mergeSharePeopleEmails([], editorEmails));
      setEmailInput('');
      toast.success('El enlace fue desactivado.');
    } catch (err) {
      console.error('CAD share: fallo al desactivar', err);
      setError('No se pudo desactivar el enlace. Intentalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareUrl || !hasActiveLink) return;
    const ok = await copyToClipboard(shareUrl);
    if (ok) toast.success('Enlace copiado en el portapapeles.');
    else toast.error('No se pudo copiar automáticamente.');
  };

  const handleRemoveInvitee = (email) => {
    const previous = emails;
    const previousEditors = editorEmails;
    setEmails((prev) => prev.filter((item) => item !== email));
    setEditorEmails((prev) => prev.filter((item) => item !== email));
    setEmailInputError('');
    toast.info('Se quitó al usuario de la lista.', {
      actionLabel: 'Deshacer',
      onAction: () => {
        setEmails(previous);
        setEditorEmails(previousEditors);
      },
      duration: 5000,
    });
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: SHARE_DIALOG_PAPER_SX }}
      {...brandsOverlap}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 2.5,
          py: 1.75,
          flexShrink: 0,
          color: TEXT_PRIMARY,
          fontFamily: typography.fontFamily.heading,
          fontSize: '18px',
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
      >
        Compartir
        <IconButton
          aria-label="Cerrar"
          onClick={handleClose}
          disabled={submitting}
          size="small"
          sx={{
            color: TEXT_SECONDARY,
            '&:hover': { color: TEXT_PRIMARY, bgcolor: 'rgba(255,255,255,0.06)' },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ borderColor: BORDER }} />

      {projectName ? (
        <Box sx={SHARE_DIALOG_LIST_META_SX}>
          <Typography
            sx={{
              color: TEXT_PRIMARY,
              fontSize: '16px',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {String(projectName).toUpperCase()}
          </Typography>
        </Box>
      ) : null}

      <DialogContent
        ref={contentRef}
        sx={{
          ...SHARE_DIALOG_CONTENT_SX,
          pt: projectName ? 1.5 : 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {isInitializing ? (
            <ShareDialogContentSkeleton />
          ) : (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography sx={{ color: TEXT_PRIMARY, fontSize: '13px', fontWeight: 600 }}>
                  Acceso
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  <AccessModeCard
                    selected={accessMode === 'public'}
                    disabled={submitting}
                    icon={<LanguageIcon sx={{ fontSize: 18 }} />}
                    title="Público"
                    description="Cualquier persona con el enlace puede ver, sin iniciar sesión."
                    onClick={() => {
                      setAccessMode('public');
                      setEmails([...editorEmails]);
                      setError('');
                    }}
                  />
                  <AccessModeCard
                    selected={accessMode === 'restricted'}
                    disabled={submitting}
                    icon={<LockOutlinedIcon sx={{ fontSize: 18 }} />}
                    title="Restringido"
                    description="Solo personas invitadas pueden acceder."
                    onClick={() => {
                      setAccessMode('restricted');
                      setEmails((prev) => mergeSharePeopleEmails(prev, editorEmails));
                      setError('');
                    }}
                  />
                </Box>
              </Box>

              <FormControlLabel
                sx={{
                  m: 0,
                  alignItems: 'flex-start',
                  gap: 1,
                  ...(!cadastralAvailable ? { opacity: 0.55 } : {}),
                }}
                control={(
                  <Switch
                    size="small"
                    checked={cadastralAvailable && showCadastralBase}
                    disabled={submitting || !cadastralAvailable || isSpaceGuest}
                    onChange={(_event, checked) => setShowCadastralBase(checked)}
                    sx={{ ...FORM_SWITCH_SX, mt: 0.25 }}
                  />
                )}
                label={(
                  <Box>
                    <Typography sx={{ color: TEXT_PRIMARY, fontSize: '13px', fontWeight: 600 }}>
                      Mostrar lotes catastrales de fondo
                    </Typography>
                    <Typography sx={{ color: TEXT_MUTED, fontSize: '12px', lineHeight: 1.4 }}>
                      {!cadastralAvailable
                        ? 'Esta lista es un plano sin ubicación en el mapa, así que no hay catastro de fondo para mostrar.'
                        : (isSpaceGuest
                          ? 'Lo define el dueño del espacio.'
                          : 'Si está desactivado, el visitante solo ve los lotes de esta lista, sin el resto del catastro.')}
                    </Typography>
                  </Box>
                )}
              />

              {/* Espacio ajeno + público: en público Personas es solo para
                  invitar editores, y eso es del dueño. */}
              {!(isSpaceGuest && accessMode === 'public') && (
              <Box sx={SHARE_SECTION_SHELL_SX}>
                  <Typography sx={{ color: TEXT_PRIMARY, fontSize: '16px', fontWeight: 500 }}>
                    Personas
                  </Typography>
                  <Typography sx={{ color: TEXT_MUTED, fontSize: '12px', lineHeight: 1.4 }}>
                    {accessMode === 'public'
                      ? 'Invitá editores. En una lista pública cualquiera con el enlace ya puede ver.'
                      : (isSpaceGuest
                        ? 'Las personas que agregues pueden ver la lista. Invitar editores es del dueño del espacio.'
                        : 'Por defecto pueden ver la lista. Cambiá el permiso a Editor si querés que editen el contenido.')}
                  </Typography>
                  <Box
                    onClick={() => inputRef.current?.focus()}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      minHeight: 44,
                      px: 1.5,
                      py: 1,
                      borderRadius: '8px',
                      bgcolor: BG_ELEVATED,
                      cursor: 'text',
                      '&:focus-within': { boxShadow: `0 0 0 1px ${colors.primary}` },
                      '& input': SHARE_EMAIL_INPUT_SX,
                    }}
                  >
                    <MailOutlineIcon sx={{ fontSize: 18, color: TEXT_SECONDARY, flexShrink: 0 }} />
                    <input
                      ref={inputRef}
                      value={emailInput}
                      onChange={handleEmailChange}
                      onKeyDown={handleEmailKeyDown}
                      onBlur={handleEmailBlur}
                      placeholder="correo@ejemplo.com.ar"
                      autoComplete="email"
                    />
                  </Box>
                  {emailInputError ? (
                    <Typography sx={{ color: DANGER, fontSize: '11px', ml: 0.5 }}>
                      {emailInputError}
                    </Typography>
                  ) : null}
                  {emails.length > 0 ? (
                    <>
                    <Box sx={INVITE_LIST_SX}>
                      {emails.map((email) => (
                        <Box
                          key={email}
                          sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minHeight: 40, px: 0.5 }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              bgcolor: colors.primary,
                              color: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              fontSize: '11px',
                              fontWeight: 700,
                            }}
                            aria-hidden
                          >
                            {getEmailInitials(email)}
                          </Box>
                          <Typography
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              color: TEXT_PRIMARY,
                              fontSize: '13px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {email}
                          </Typography>
                          {accessMode === 'public' || isSpaceGuest ? (
                            <Typography
                              sx={{
                                flexShrink: 0,
                                color: TEXT_SECONDARY,
                                fontSize: '12px',
                                px: 1,
                              }}
                            >
                              {accessMode === 'public' ? 'Editor' : 'Puede ver'}
                            </Typography>
                          ) : (
                            <SharePersonRoleSelect
                              email={email}
                              disabled={submitting}
                              value={editorEmails.includes(email) ? SHARE_PERSON_ROLE_EDITOR : SHARE_PERSON_ROLE_VIEWER}
                              onChange={(role) => {
                                setEditorEmails((prev) => {
                                  if (role === SHARE_PERSON_ROLE_EDITOR) {
                                    return prev.includes(email) ? prev : [...prev, email];
                                  }
                                  return prev.filter((item) => item !== email);
                                });
                              }}
                            />
                          )}
                          <Tooltip title={accessMode === 'public' ? 'Quitar editor' : 'Quitar acceso'} placement="top" arrow>
                            <IconButton
                              aria-label={accessMode === 'public' ? `Quitar editor ${email}` : `Quitar acceso a ${email}`}
                              size="small"
                              disabled={submitting}
                              onClick={() => handleRemoveInvitee(email)}
                              sx={{
                                color: TEXT_SECONDARY,
                                p: 0.5,
                                '&:hover': { color: TEXT_PRIMARY, bgcolor: 'rgba(255,255,255,0.06)' },
                              }}
                            >
                              <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ))}
                    </Box>
                    {hasNewInvitees ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        mt: 0.5,
                        pt: 1,
                        borderTop: `1px solid ${BORDER}`,
                      }}
                    >
                      <Checkbox
                        checked={notifyUsers}
                        onChange={(event) => {
                          const checked = event.target.checked;
                          setNotifyUsers(checked);
                          if (!checked) setNotifyMessageError('');
                        }}
                        disabled={submitting}
                        size="small"
                        sx={{
                          p: 0,
                          ml: 0,
                          color: TEXT_SECONDARY,
                          '&.Mui-checked': { color: colors.primary },
                        }}
                      />
                      <Typography sx={{ color: TEXT_PRIMARY, fontSize: '13px' }}>
                        Notificar a los usuarios
                      </Typography>
                    </Box>
                    ) : null}
                    {hasNewInvitees && notifyUsers ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.5 }}>
                        <Typography sx={{ color: TEXT_PRIMARY, fontSize: '13px', fontWeight: 500 }}>
                          Mensaje
                        </Typography>
                        <Box
                          component="textarea"
                          value={notifyMessage}
                          onChange={(event) => {
                            setNotifyMessage(event.target.value.slice(0, 2000));
                            if (notifyMessageError) setNotifyMessageError('');
                          }}
                          placeholder="Escribí un mensaje para los nuevos invitados"
                          rows={3}
                          maxLength={2000}
                          disabled={submitting}
                          sx={{
                            width: '100%',
                            boxSizing: 'border-box',
                            resize: 'vertical',
                            minHeight: 72,
                            px: 1.5,
                            py: 1.25,
                            borderRadius: '8px',
                            border: `1px solid ${notifyMessageError ? DANGER : BORDER}`,
                            bgcolor: BG_ELEVATED,
                            color: TEXT_PRIMARY,
                            fontFamily: typography.fontFamily.primary,
                            fontSize: '13px',
                            lineHeight: 1.4,
                            outline: 'none',
                            opacity: submitting ? 0.7 : 1,
                            '&::placeholder': { color: TEXT_MUTED },
                            '&:focus': {
                              borderColor: notifyMessageError ? DANGER : colors.primary,
                            },
                          }}
                        />
                        {notifyMessageError ? (
                          <Typography sx={{ color: DANGER, fontSize: '12px' }}>
                            {notifyMessageError}
                          </Typography>
                        ) : null}
                      </Box>
                    ) : null}
                    </>
                  ) : null}
                </Box>
              )}

              <Box sx={SHARE_SECTION_SHELL_SX}>
                <Typography sx={{ color: TEXT_PRIMARY, fontSize: '16px', fontWeight: 500 }}>
                  Enlace
                </Typography>
                <Typography sx={{ color: '#D4D4D4', fontSize: '12px', lineHeight: 1.4 }}>
                  {accessMode === 'restricted'
                    ? 'Solo personas con acceso pueden ver.'
                    : hasActiveLink
                      ? 'Copiá el enlace y compartilo con quien quieras. Hace falta iniciar sesión.'
                      : 'Todavía no existe un enlace para compartir esta lista.'}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    minHeight: 44,
                    px: 1.25,
                    borderRadius: '8px',
                    bgcolor: BG_ELEVATED,
                    opacity: hasActiveLink ? 1 : 0.55,
                  }}
                >
                  {hasActiveLink
                    ? <LinkIcon sx={{ fontSize: 18, color: TEXT_SECONDARY, flexShrink: 0 }} />
                    : <LinkOffIcon sx={{ fontSize: 18, color: TEXT_MUTED, flexShrink: 0 }} />}
                  <Typography
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      color: hasActiveLink ? TEXT_PRIMARY : TEXT_MUTED,
                      fontSize: '12px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {hasActiveLink
                      ? shareUrl
                      : 'Generá un enlace para que cualquier persona pueda acceder.'}
                  </Typography>
                  {hasActiveLink ? (
                    <Button
                      size="small"
                      onClick={handleCopyLink}
                      startIcon={<ContentCopyIcon sx={{ fontSize: '16px !important' }} />}
                      sx={{
                        flexShrink: 0,
                        textTransform: 'none',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: colors.primaryLight,
                        minWidth: 0,
                        px: 1,
                        '&:hover': { bgcolor: 'rgba(72,146,145,0.12)' },
                      }}
                    >
                      Copiar
                    </Button>
                  ) : null}
                </Box>
              </Box>

              <ShareTemplateSection
                value={shareTemplate}
                brandId={brandId}
                onChange={(nextTemplate, nextBrandId) => {
                  setShareTemplate(nextTemplate);
                  setBrandId(nextBrandId);
                }}
                disabled={submitting}
                locked={isSpaceGuest}
              />

              {error ? (
                <Typography sx={{ color: DANGER, fontSize: '12px' }}>{error}</Typography>
              ) : null}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.75,
          gap: 1,
          justifyContent: 'flex-end',
          ...getShareDialogFooterScrollHintSx(canScrollMore),
        }}
      >
        {isInitializing ? (
          <Skeleton variant="rounded" width={140} height={40} sx={skFooterButtonSx} />
        ) : isPrivate ? (
          <>
            {hasActiveLink ? (
              <Button
                onClick={handleRevoke}
                disabled={submitting || guestBlocked}
                variant="outlined"
                startIcon={<LinkOffIcon sx={{ fontSize: '16px !important' }} />}
                sx={getShareDangerButtonSx({ busy: submitting })}
              >
                Desactivar enlace
              </Button>
            ) : null}
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting || isInitializing || privatePrimaryDisabled || guestBlocked}
              sx={getSharePrimaryButtonSx({
                busy: (submitting || isInitializing) && !privatePrimaryDisabled,
              })}
            >
              {privatePrimaryLabel}
            </Button>
          </>
        ) : hasActiveLink ? (
          <>
            <Button
              onClick={handleRevoke}
              disabled={submitting || guestBlocked}
              variant="outlined"
              startIcon={<LinkOffIcon sx={{ fontSize: '16px !important' }} />}
              sx={getShareDangerButtonSx({ busy: submitting })}
            >
              Desactivar enlace
            </Button>
            {publicDirty || (notifyUsers && hasNewInvitees) ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting || guestBlocked}
                sx={getSharePrimaryButtonSx({ busy: submitting })}
              >
                Actualizar
              </Button>
            ) : null}
          </>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || isInitializing || guestBlocked}
            startIcon={<LinkIcon sx={{ fontSize: '16px !important' }} />}
            sx={getSharePrimaryButtonSx({ busy: submitting || isInitializing })}
          >
            Generar enlace
          </Button>
        )}
      </DialogActions>
    </Dialog>
    {guardDialog}
    </>
  );
}
